import React, { useMemo, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { ICustomer } from "../../Api/Interface/api.interface";

// ── localStorage helpers ───────────────────────────────────────────────────────
const STORAGE_KEY = "tjbus_recent_clients";
const MAX_RECENT  = 5;

const loadRecentIds = (): number[] => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]"); }
  catch { return []; }
};

const persistRecentIds = (ids: number[]) =>
  localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));

// ── Display helpers ────────────────────────────────────────────────────────────
const getDisplayName = (c: ICustomer): string => {
  const company = c.customerCompanyName?.trim();
  if (company && company.toLowerCase() !== "null") return company;
  const fullName = [c.customerFirstName, c.customerLastName]
    .filter((s) => s && s.trim() && s.trim().toLowerCase() !== "null")
    .map((s) => s!.trim())
    .join(" ");
  return fullName || "";
};

const getInitials = (c: ICustomer): string => {
  const name = getDisplayName(c);
  const words = name.split(" ").filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return "?";
};

const AVATAR_PALETTE = [
  "#2f3891", "#2f3891", "#ec4899", "#14b8a6",
  "#f59e0b", "#10b981", "#3b82f6", "#ef4444",
];
const avatarBg = (id: number) => AVATAR_PALETTE[id % AVATAR_PALETTE.length];

// ── Component ──────────────────────────────────────────────────────────────────
interface Props {
  customers: ICustomer[];
  value: ICustomer | null;
  onChange: (c: ICustomer | null) => void;
  label?: string;
  required?: boolean;
  loading?: boolean;
}

const CrmClientSelect: React.FC<Props> = ({
  customers,
  value,
  onChange,
  label = "Client",
  required,
  loading,
}) => {
  const [recentIds, setRecentIds] = useState<number[]>(loadRecentIds);

  // Build ordered list: recents first, then the rest (no duplicates)
  const orderedOptions = useMemo(() => {
    const recentSet = new Set(recentIds);
    const recent = recentIds
      .map((rid) => customers.find((c) => c.id === rid))
      .filter(Boolean) as ICustomer[];
    const rest = customers.filter((c) => !recentSet.has(c.id));
    return [...recent, ...rest];
  }, [customers, recentIds]);

  const handleChange = (_: React.SyntheticEvent, v: ICustomer | null) => {
    if (v) {
      const newIds = [v.id, ...recentIds.filter((x) => x !== v.id)].slice(0, MAX_RECENT);
      persistRecentIds(newIds);
      setRecentIds(newIds);
    }
    onChange(v);
  };

  return (
    <Autocomplete
      options={orderedOptions}
      value={value}
      onChange={handleChange}
      groupBy={(c) => (recentIds.includes(c.id) ? "Recent Clients" : "All Clients")}
      getOptionLabel={getDisplayName}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={(opts, state) => {
        const q = state.inputValue.toLowerCase().trim();
        if (!q) return opts;
        return opts.filter((c) => {
          const name    = getDisplayName(c).toLowerCase();
          const company = (c.customerCompanyName ?? "").toLowerCase();
          const phone   = (c.customerMobileNum ?? "").toLowerCase();
          const email   = (c.customerEmail ?? "").toLowerCase();
          return (
            name.includes(q) ||
            company.includes(q) ||
            phone.includes(q) ||
            email.includes(q)
          );
        });
      }}
      noOptionsText={
        <Typography variant="body2" color="text.secondary" sx={{ py: 1, textAlign: "center" }}>
          No clients found
        </Typography>
      }
      renderOption={(props, c) => {
        const name    = getDisplayName(c);
        const phone   = c.customerMobileNum?.trim()   || null;
        const email   = c.customerEmail?.trim()       || null;
        const company = c.customerCompanyName?.trim() || null;

        return (
          <Box
            component="li"
            {...props}
            key={c.id}
            sx={{ alignItems: "flex-start !important", py: "10px !important", px: "12px !important" }}
          >
            {/* Initials avatar */}
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: avatarBg(c.id),
                fontSize: 13,
                fontWeight: 700,
                mr: 1.5,
                flexShrink: 0,
                mt: 0.25,
              }}
            >
              {getInitials(c)}
            </Avatar>

            {/* Details */}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="body2"
                fontWeight={600}
                color="#0f172a"
                noWrap
                sx={{ lineHeight: 1.4 }}
              >
                {name}
              </Typography>
              {company && (
                <Typography variant="caption" color="#64748b" display="block" noWrap sx={{ lineHeight: 1.5 }}>
                  🏢 {company}
                </Typography>
              )}
              {phone && (
                <Typography variant="caption" color="#64748b" display="block" noWrap sx={{ lineHeight: 1.5 }}>
                  📞 {phone}
                </Typography>
              )}
              {email && (
                <Typography variant="caption" color="#64748b" display="block" noWrap sx={{ lineHeight: 1.5 }}>
                  ✉️ {email}
                </Typography>
              )}
            </Box>
          </Box>
        );
      }}
      renderGroup={(params) => (
        <Box key={params.key}>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              px: 2,
              py: 0.75,
              fontWeight: 700,
              color: "#2f3891",
              bgcolor: "#f5f3ff",
              borderBottom: "1px solid #e2e8f0",
              borderTop: params.group === "All Clients" ? "2px solid #e2e8f0" : "none",
              letterSpacing: 0.8,
              textTransform: "uppercase",
              fontSize: 10,
            }}
          >
            {params.group === "Recent Clients" ? "⏱ Recent Clients" : "👥 All Clients"}
          </Typography>
          {params.children}
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={required ? `${label} *` : label}
          size="small"
          placeholder="Search by name, company, phone, email…"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      ListboxProps={{ style: { maxHeight: 420, padding: 0 } }}
    />
  );
};

export default CrmClientSelect;
